package fr.frux.core.designsystem.components

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBars
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Add
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.NavigationBarItem
import androidx.compose.material3.NavigationBarItemDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.resources.Res
import fr.frux.core.designsystem.resources.ds_bottom_nav_add_tree
import org.jetbrains.compose.resources.stringResource

data class FruxNavItem<T>(
    val route: T,
    val label: String,
    val icon: ImageVector,
)

/**
 * Dégagement bas réellement occupé par [FruxBottomNav], quand elle est posée dans le slot
 * `bottomBar` d'un `Scaffold` racine (#85) : la valeur vient du `PaddingValues` que ce `Scaffold`
 * calcule lui-même à partir de la hauteur mesurée de la barre (elle-même déjà pas nulle quand la
 * barre est absente de l'écran courant). Fournie par `App` autour du contenu navigué ; vaut 0 par
 * défaut (aperçus, tests) pour qu'un composable qui l'utilise sans provider explicite ne réserve
 * rien plutôt que de lever une erreur.
 */
val LocalBottomNavPadding = staticCompositionLocalOf { 0.dp }

object FruxBottomNavDefaults {
    /** Coins supérieurs très arrondis : la barre se lit comme la base d'un bottom sheet posé sur
     * le fond, cohérents avec [MaterialTheme.shapes] `extraLarge`. */
    private val TopCornerRadius = 28.dp
    val Shape = RoundedCornerShape(topStart = TopCornerRadius, topEnd = TopCornerRadius)

    /** Feuille qui flotte au-dessus du bord bas (grand écran) : les quatre coins arrondis. */
    val FloatingShape = RoundedCornerShape(TopCornerRadius)

    /**
     * Pas d'ombre propre à la barre : quand un sheet de carte s'étend jusque sous elle (surface
     * blanche continue, #205), l'ombre dessinait une ligne grise visible sur le sheet. La forme
     * [Shape] (coins arrondis) suffit à détacher la barre du fond quand aucun sheet n'est présent.
     * Le sheet, lui, garde son ombre — cf. [SheetElevation] — car il n'a rien sous lui pour la
     * porter à sa place.
     */
    val Elevation = 0.dp

    /** Ombre portée vers le haut des sheets de carte ([fr.frux.feature.map.MapSheetSurface]) —
     * distincte de [Elevation] depuis #205 : eux n'ont rien en dessous pour porter une ombre, la
     * barre si (le sheet lui-même, quand il s'étend jusque sous elle). */
    val SheetElevation = 3.dp

    /** Diamètre de la pastille centrale d'ajout. */
    val AddButtonSize = 56.dp

    /**
     * The bar's own content height: M3's default `NavigationBar` height (80dp), appliquée comme
     * hauteur minimale — le même comportement que l'ancien `NavigationBar` qu'elle remplace. Ne
     * représente pas l'espace qu'occupe la barre à l'écran sous edge-to-edge (elle applique en
     * plus l'inset `navigationBars` en interne) : un écran qui a besoin de ce dégagement lit
     * [LocalBottomNavPadding], posé par le `Scaffold` racine qui héberge la barre (#85).
     */
    val Height = 80.dp
}

/**
 * Bottom navigation "base de bottom sheet" (#199) : surface pleine largeur, coins supérieurs
 * arrondis, avec un 5e emplacement central — une pastille « + » — au milieu des 4 onglets.
 *
 * Les 4 onglets restent de vrais [NavigationBarItem] M3 (sémantique `Role.Tab`, indicateur de
 * sélection, cible tactile 48dp) : seul le conteneur change, un `Row` habillé par [Surface] au
 * lieu du `NavigationBar` de M3, pour pouvoir insérer la pastille centrale au milieu de la
 * `RowScope` que ces items exigent.
 */
@Composable
fun <T> FruxBottomNav(
    items: List<FruxNavItem<T>>,
    isSelected: (FruxNavItem<T>) -> Boolean,
    onSelect: (FruxNavItem<T>) -> Unit,
    onAddClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = FruxBottomNavDefaults.Shape,
        color = MaterialTheme.colorScheme.surface,
        shadowElevation = FruxBottomNavDefaults.Elevation,
    ) {
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .windowInsetsPadding(WindowInsets.navigationBars)
                    .defaultMinSize(minHeight = FruxBottomNavDefaults.Height),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            // 4 onglets fixes (#199, composition inchangée) : la pastille se pose au milieu, entre
            // les deux premiers et les deux derniers.
            val centerIndex = items.size / 2
            items.forEachIndexed { index, item ->
                if (index == centerIndex) {
                    AddTreeButton(onClick = onAddClick)
                }
                val selected = isSelected(item)
                NavigationBarItem(
                    selected = selected,
                    onClick = { onSelect(item) },
                    icon = {
                        Icon(imageVector = item.icon, contentDescription = item.label)
                    },
                    label = {
                        Text(text = item.label, style = MaterialTheme.typography.labelMedium)
                    },
                    colors =
                        NavigationBarItemDefaults.colors(
                            indicatorColor = MaterialTheme.colorScheme.primaryContainer,
                            selectedIconColor = MaterialTheme.colorScheme.primary,
                            selectedTextColor = MaterialTheme.colorScheme.primary,
                            unselectedIconColor = MaterialTheme.colorScheme.onSurfaceVariant,
                            unselectedTextColor = MaterialTheme.colorScheme.onSurfaceVariant,
                        ),
                )
            }
        }
    }
}

/**
 * Pastille « + » centrale, 5e emplacement de [FruxBottomNav] : action directe (jamais
 * `selected`), pas un onglet — sémantique bouton et non tab, `contentDescription` explicite
 * puisque l'icône seule ne porte aucun texte.
 */
@Composable
internal fun AddTreeButton(
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        onClick = onClick,
        modifier = modifier.size(FruxBottomNavDefaults.AddButtonSize),
        shape = CircleShape,
        color = MaterialTheme.colorScheme.primary,
        contentColor = MaterialTheme.colorScheme.onPrimary,
    ) {
        Box(contentAlignment = Alignment.Center) {
            Icon(
                imageVector = Icons.Outlined.Add,
                contentDescription = stringResource(Res.string.ds_bottom_nav_add_tree),
            )
        }
    }
}
